"use client"

import { useEffect, useRef } from "react"
import { createClient } from "@/lib/supabase/client"
import { useAuth } from "@/lib/auth/context"

/**
 * UserActivityTracker
 * 
 * Periodically updates the current user's last_seen_at column 
 * in the users table to provide "online" status information.
 */
export function UserActivityTracker() {
  const { user } = useAuth()
  const lastUpdateRef = useRef<number>(0)
  const UPDATE_INTERVAL = 60 * 1000 // Update every minute

  useEffect(() => {
    if (!user) return

    const supabase = createClient()
    
    const updateActivity = async () => {
      const now = Date.now()
      // Throttle updates to at most once per minute
      if (now - lastUpdateRef.current < UPDATE_INTERVAL) return

      try {
        await supabase
          .from("users")
          .update({ last_seen_at: new Date().toISOString() })
          .eq("auth_id", user.id)
        
        lastUpdateRef.current = now
      } catch (error) {
        // Silent fail for activity tracking
        console.warn("Failed to update user activity status")
      }
    }

    // Initial update
    updateActivity()

    // Listen for visibility changes (e.g., app coming back to foreground)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        updateActivity()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    
    // Periodically update while active
    const intervalId = setInterval(updateActivity, UPDATE_INTERVAL)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      clearInterval(intervalId)
    }
  }, [user])

  return null
}
